import { z } from 'zod';
import { AreaValueSchema, PercentageSchema } from './measurements';
import { MonetaryValueSchema, MoneyPerAreaValueSchema } from './money';
export const RentSummarySchema = z.object({
    monetary_value: MonetaryValueSchema,
    value_per_area: MoneyPerAreaValueSchema.optional(),
    value_per_unit: MonetaryValueSchema.optional(),
});
export const TenantSummaryUsageTypeSchema = z.enum([
    'residential',
    'office',
    'retail',
    'logistics',
    'parking',
    'other',
    'commercial',
    'gastronomy',
    'industry',
    'hotel',
    'storage',
    'underground_parking',
    'leisure',
    'social',
    'archive',
    'outdoor_parking',
    'garage_parking',
    'other_parking',
    'other_use',
    'open_space',
]);
export const TenantSummaryItemSchema = z.object({
    type: TenantSummaryUsageTypeSchema,
    number_of_units: z.number(),
    vacancy_number_of_units: z.number().int().nonnegative(),
    total_area: AreaValueSchema,
    vacancy_area: AreaValueSchema,
    actual_rental_income: MonetaryValueSchema,
    gross_rental_income: MonetaryValueSchema,
    net_rental_income: MonetaryValueSchema,
    management_cost: MonetaryValueSchema,
    administrative_cost: MonetaryValueSchema,
    maintenance_cost: MonetaryValueSchema,
    operating_cost: MonetaryValueSchema,
    loss_of_rent_risk_cost: MonetaryValueSchema,
    discount_rate: PercentageSchema,
});
export const TenantSummaryManagementCostScopeSchema = z.enum(['per_area', 'absolute', 'per_yearly_rent']);
export const OverrideTotalTenantSummaryItemSchema = z.object({
    type: z.literal('override_total'),
    management_cost: MonetaryValueSchema,
    administrative_cost: MonetaryValueSchema,
    administrative_cost_percentage: PercentageSchema,
    administrative_cost_scope: TenantSummaryManagementCostScopeSchema,
    maintenance_cost: MonetaryValueSchema,
    maintenance_cost_percentage: PercentageSchema,
    maintenance_cost_scope: TenantSummaryManagementCostScopeSchema,
    operating_cost: MonetaryValueSchema,
    operating_cost_percentage: PercentageSchema,
    operating_cost_scope: TenantSummaryManagementCostScopeSchema,
    loss_of_rent_risk_cost: MonetaryValueSchema,
    loss_of_rent_risk_cost_percentage: PercentageSchema,
    loss_of_rent_risk_cost_scope: TenantSummaryManagementCostScopeSchema,
    net_rental_income: MonetaryValueSchema,
    gross_rental_income: MonetaryValueSchema,
    actual_rental_income: MonetaryValueSchema,
});
export const RootTenantSummaryItemSchema = z.union([
    TenantSummaryItemSchema,
    OverrideTotalTenantSummaryItemSchema,
]);
export const TenantSummarySchema = z.record(z.string(), RootTenantSummaryItemSchema.optional());
export const ValueOverviewTenantSummaryUsageTypeSchema = z.union([TenantSummaryUsageTypeSchema, z.literal('total')]);
export const TenantItemMonetaryValueSchema = z.object({
    total: MonetaryValueSchema,
    per_unit: MonetaryValueSchema.optional(),
    per_unit_monthly: MonetaryValueSchema.optional(),
    per_sqm: MonetaryValueSchema.optional(),
    per_sqm_monthly: MonetaryValueSchema.optional(),
});
export const ValueOverviewTenantSummaryItemSchema = z.object({
    type: ValueOverviewTenantSummaryUsageTypeSchema,
    number_of_units: z.number(),
    total_area: AreaValueSchema.optional(),
    vacancy_area: AreaValueSchema.optional(),
    vacancy_number_of_units: z.number().int().nonnegative(),
    vacancy_percentage: PercentageSchema.optional(),
    actual_rental_income: TenantItemMonetaryValueSchema,
    gross_rental_income: TenantItemMonetaryValueSchema,
    net_rental_income: TenantItemMonetaryValueSchema,
    management_cost: TenantItemMonetaryValueSchema,
    administrative_cost: TenantItemMonetaryValueSchema,
    maintenance_cost: TenantItemMonetaryValueSchema,
    operating_cost: TenantItemMonetaryValueSchema,
    loss_of_rent_risk_cost: TenantItemMonetaryValueSchema,
    discount_rate: z.number(),
});
export const ValueOverviewOverrideTotalTenantSummaryItemSchema = z.object({
    type: z.literal('override_total'),
    management_cost: MonetaryValueSchema,
    administrative_cost: MonetaryValueSchema,
    maintenance_cost: MonetaryValueSchema,
    operating_cost: MonetaryValueSchema,
    loss_of_rent_risk_cost: MonetaryValueSchema,
    net_rental_income: MonetaryValueSchema,
    gross_rental_income: MonetaryValueSchema,
    actual_rental_income: MonetaryValueSchema,
});
export const AllTenantUsageTypesSchema = z.union([
    ValueOverviewTenantSummaryUsageTypeSchema,
    ValueOverviewOverrideTotalTenantSummaryItemSchema.shape.type,
]);
const valueOverviewTenantSummaryRecordShape = (itemSchema) => ({
    residential: itemSchema.optional(),
    office: itemSchema.optional(),
    retail: itemSchema.optional(),
    logistics: itemSchema.optional(),
    parking: itemSchema.optional(),
    other: itemSchema.optional(),
    total: itemSchema.optional(),
    commercial: itemSchema.optional(),
    gastronomy: itemSchema.optional(),
    industry: itemSchema.optional(),
    hotel: itemSchema.optional(),
    storage: itemSchema.optional(),
    underground_parking: itemSchema.optional(),
    leisure: itemSchema.optional(),
    social: itemSchema.optional(),
    archive: itemSchema.optional(),
    outdoor_parking: itemSchema.optional(),
    other_parking: itemSchema.optional(),
    garage_parking: itemSchema.optional(),
    other_use: itemSchema.optional(),
    open_space: itemSchema.optional(),
});
export const ValueOverviewRootTenantSummaryItemSchema = z.union([
    ValueOverviewTenantSummaryItemSchema,
    ValueOverviewOverrideTotalTenantSummaryItemSchema,
]);
const ValueOverviewTenantSummaryShape = {
    ...valueOverviewTenantSummaryRecordShape(ValueOverviewTenantSummaryItemSchema),
    override_total: ValueOverviewOverrideTotalTenantSummaryItemSchema.optional(),
};
export const ValueOverviewTenantSummarySchema = z.object(ValueOverviewTenantSummaryShape);
const AggregatedValueOverviewTenantSummaryShape = {
    ...valueOverviewTenantSummaryRecordShape(ValueOverviewRootTenantSummaryItemSchema),
    override_total: ValueOverviewRootTenantSummaryItemSchema.optional(),
};
export const AggregatedValueOverviewTenantSummarySchema = z.object(AggregatedValueOverviewTenantSummaryShape);
